/**
 * Field Observations — service layer for the Field Observation document tool
 * (Construction Gates Phase B). Backed by api/field-observations.js (Supabase
 * `field_observations` + `field_observation_items` + the public `field-observations` bucket).
 *
 * One observation per MILESTONE — created lazily on first open, never deleted or switched
 * (the Site Analysis one-per-project model, scoped per-milestone instead).
 */

async function authHeaders() {
  const token = await window.__clerkGetToken()
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
}

async function call(method, body, qs) {
  const res = await fetch('/api/field-observations' + (qs ? '?' + qs : ''), {
    method,
    headers: await authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Field observation request failed (' + res.status + ')'))
  return data
}

/** Get-or-create the milestone's observation + its captured items. */
export async function getOrCreateFieldObservation(msId, projectId) {
  const qs = 'milestoneId=' + encodeURIComponent(msId) + (projectId ? '&projectId=' + encodeURIComponent(projectId) : '')
  return call('GET', null, qs)
}

/** Signed upload URL for one photo into the `field-observations` bucket. */
export async function getFieldObservationPhotoUploadUrl(observationId, filename) {
  return call('POST', { action: 'photoUploadUrl', observationId, filename })
}

async function putSigned(signedUrl, file) {
  const res = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' }, body: file })
  if (!res.ok) throw new Error('Photo upload failed (' + res.status + ')')
}

/** Upload a photo File and return its stored path (append to an item's photoPaths). */
export async function uploadFieldObservationPhoto(observationId, file) {
  const { signedUrl, path } = await getFieldObservationPhotoUploadUrl(observationId, file.name)
  await putSigned(signedUrl, file)
  return path
}

/**
 * Create or update a capture item (idempotent on item.id — pass a stable client-generated uuid,
 * e.g. the checklist item's own id for a checklist-guided row).
 */
export async function upsertFieldObservationItem(observationId, item, byName) {
  const { item: saved } = await call('POST', { action: 'upsertItem', observationId, item, byName })
  return saved
}

export function deleteFieldObservationItem(itemId) {
  return call('POST', { action: 'deleteItem', itemId })
}

/** Move the observation between 'draft' (still capturing) and 'issued' (studio-reviewed QA/QC gate). */
export async function setFieldObservationStatus(observationId, status) {
  const { observation } = await call('POST', { action: 'setStatus', observationId, status })
  return observation
}

/** On-demand PDF preview (any status) — opens as a blob, matching the app's other preview buttons. */
export async function previewFieldObservationPdf(msId) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/field-observations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ action: 'pdf', milestoneId: msId, working: true }),
  })
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'PDF generation failed') }
  return res.blob()
}

/**
 * Publish the ISSUED observation's PDF into the posted milestone's Documents (source
 * 'field-observation'). No-ops silently if there's no observation yet or it was never issued —
 * called unconditionally + best-effort on every post of a FIELD_OBSERVATION_MS_TYPES milestone.
 */
export function freezeFieldObservation(projectId, msId) {
  return call('POST', { action: 'freeze', projectId, milestoneId: msId })
}
